/*
  HCS recently viewed — records the shopper's product history in a first-party
  cookie and renders it back wherever the "Recently viewed" section is placed.
  3 September 2026, desk 08.

  TWO JOBS, ON DIFFERENT PAGES, WHICH IS WHY THIS LOADS SITEWIDE:
    1. RECORD  — on every product page, push the handle onto the cookie.
    2. RENDER  — wherever the rail section exists, fetch and fill it.
  Loading it only on the homepage would mean nothing was ever recorded and the
  rail would stay permanently empty.

  ⚑ WHY A COOKIE. Finbar asked for cookies, and it is the right call: first
  party, on the store's own domain, expires on its own. Capped at 12 handles so
  it can never bloat the request headers — under 500 bytes.

  ⚑ WHY ONE REQUEST PER CARD AND NOT ONE /search CALL FOR ALL OF THEM.
  Liquid cannot read a cookie, so cards must be fetched. The tempting shortcut
  is /search?q=<handles>&section_id=… because `search.terms` is the only way to
  hand Liquid an arbitrary string — but that RECORDS A STOREFRONT SEARCH FOR A
  LIST OF HANDLES ON EVERY RETURNING VISITOR'S HOMEPAGE LOAD. Site-search data
  is live evidence on this desk (Q-08AL rests on 1,134 real searches over 180
  days) and a fake search per session would swamp it while looking exactly like
  real shopper behaviour. Eight small parallel requests are the cheaper
  mistake. ⚑ Do not "optimise" this back to one call.

  ⚑ IT FAILS SILENTLY AND SAFELY, ALWAYS. No history, a failed fetch, a
  product unpublished since it was viewed — every path REMOVES the section
  rather than leaving an empty band. The worst case is a homepage with no rail,
  never a broken one.
*/
(function () {
  'use strict';

  var COOKIE = 'hcs_rv';
  var KEEP = 12; // handles stored in the cookie
  var DAYS = 30;
  var HANDLE_RE = /^[a-z0-9][a-z0-9-]{0,120}$/;

  /* ---------------- cookie ---------------- */

  function read() {
    var m = document.cookie.match(/(?:^|;\s*)hcs_rv=([^;]*)/);
    if (!m) return [];
    var raw;
    try {
      raw = decodeURIComponent(m[1]);
    } catch (e) {
      return []; // a half-written or foreign value; treat as no history
    }
    return raw.split('|').filter(function (h) {
      // Handles are lowercase alphanumeric plus hyphens. ⚑ Anything else is
      // not ours and must never reach a URL we build.
      return HANDLE_RE.test(h);
    });
  }

  function write(list) {
    document.cookie =
      COOKIE +
      '=' +
      encodeURIComponent(list.slice(0, KEEP).join('|')) +
      '; path=/; expires=' +
      new Date(Date.now() + DAYS * 864e5).toUTCString() +
      '; SameSite=Lax' +
      (location.protocol === 'https:' ? '; Secure' : '');
  }

  /* ---------------- 1. record ---------------- */

  function currentHandle() {
    // Matches /products/x and /collections/y/products/x, with or without a
    // locale prefix. Stops at ? so ?variant= never becomes part of the handle.
    var m = location.pathname.match(/\/products\/([^\/?#]+)/);
    if (!m) return null;
    var h;
    try {
      h = decodeURIComponent(m[1]).toLowerCase();
    } catch (e) {
      return null;
    }
    return HANDLE_RE.test(h) ? h : null;
  }

  function record() {
    var handle = currentHandle();
    if (!handle) return;
    var list = read().filter(function (h) {
      return h !== handle;
    });
    list.unshift(handle);
    write(list);
  }

  /* ---------------- 2. render ---------------- */

  function num(el, attr, fallback) {
    var v = parseInt(el.getAttribute(attr), 10);
    return isNaN(v) ? fallback : v;
  }

  // ⚑ TWO ENDPOINTS, BECAUSE THE DOCS ARE AMBIGUOUS ABOUT ONE OF THEM.
  // sections/hcs-rv-card.liquid sits in NO template — it exists only to be
  // fetched. Shopify documents `?section_id=` as 404ing only when the id
  // "doesn't exist on the theme" (file-based, so ours should resolve), and
  // documents `?sections=` as falling back to "any default values" for a
  // section not in a template (which also implies ours resolves). Neither
  // sentence is a promise. ⚑ So we PROVE the instrument instead of assuming
  // it: try section_id, and on the first failure fall back to sections and
  // remember which one answered. That is this desk's own recorded lesson —
  // curl the endpoint once before drawing a conclusion from silence.
  var mode = null; // null = untried, then 'section_id' or 'sections'

  function liFrom(html) {
    if (!html || html.indexOf('<li') === -1) return null;
    // A Liquid error comes back as TEXT, not markup. Parse and require a real
    // element rather than trusting the string.
    var host = document.createElement('ul');
    host.innerHTML = html;
    return host.querySelector('li[data-hcs-rv-item]') || null;
  }

  function base() {
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    if (root.charAt(root.length - 1) !== '/') root += '/';
    return root; // locale-aware, per Shopify's own guidance
  }

  function tryOne(handle, which) {
    var url =
      base() +
      'products/' +
      encodeURIComponent(handle) +
      (which === 'sections'
        ? '?sections=hcs-rv-card'
        : '?section_id=hcs-rv-card');
    return fetch(url)
      .then(function (r) {
        return r.ok ? r.text() : null;
      })
      .then(function (text) {
        if (!text) return null;
        if (which === 'sections') {
          try {
            // Sections that fail to render come back as null with a 200.
            return liFrom(JSON.parse(text)['hcs-rv-card']);
          } catch (e) {
            return null;
          }
        }
        return liFrom(text);
      })
      .catch(function () {
        return null;
      });
  }

  function fetchCard(handle) {
    if (mode) return tryOne(handle, mode);
    return tryOne(handle, 'section_id').then(function (li) {
      if (li) {
        mode = 'section_id';
        return li;
      }
      return tryOne(handle, 'sections').then(function (li2) {
        if (li2) mode = 'sections';
        return li2;
      });
    });
  }

  function render(root) {
    var limit = num(root, 'data-hcs-rv-show', 8);
    var handles = read().slice(0, limit);

    // ⚑ Never offer the shopper the page they are already standing on.
    var here = currentHandle();
    if (here) {
      handles = handles.filter(function (h) {
        return h !== here;
      });
    }

    var tpl = root.querySelector('[data-hcs-rv-template]');
    var slot = root.querySelector('[data-hcs-rv-slot]');
    if (!handles.length || !tpl || !slot || !('content' in tpl)) {
      root.remove();
      return;
    }

    Promise.all(handles.map(fetchCard)).then(function (items) {
      // Promise.all preserves order, so the rail stays newest-first.
      var cards = items.filter(Boolean);
      if (!cards.length) {
        root.remove();
        return;
      }

      var frag = tpl.content.cloneNode(true);
      var list = frag.querySelector('[data-hcs-rv-list]');
      var buttons = frag.querySelector('[data-hcs-rv-buttons]');
      var total = frag.querySelector('[data-hcs-rv-total]');
      var slider = frag.querySelector('slider-component');
      if (!list || !slider) {
        root.remove();
        return;
      }

      cards.forEach(function (li, i) {
        li.id = 'Slide-' + (root.getAttribute('data-hcs-rv') || 'rv') + '-' + (i + 1);
        list.appendChild(li);
      });

      // ⚑ The slider classes must answer to how many cards ACTUALLY came back,
      // not how many were asked for. A product unpublished since it was viewed
      // would otherwise leave arrows on a row with nothing to scroll.
      var n = cards.length;
      var desktop = root.getAttribute('data-hcs-rv-desktop-slider') === '1' &&
        n > num(root, 'data-hcs-rv-cols-desktop', 4);
      var mobile = root.getAttribute('data-hcs-rv-mobile-slider') === '1' &&
        n > num(root, 'data-hcs-rv-cols-mobile', 2);

      if (desktop || mobile) {
        list.classList.add('slider');
        if (desktop) list.classList.add('slider--desktop');
        if (mobile) list.classList.add('slider--tablet', 'grid--peek');
        cards.forEach(function (li) {
          li.classList.add('slider__slide');
        });
        if (buttons) buttons.removeAttribute('hidden');
        if (total) total.textContent = String(n);
      } else {
        cards.forEach(function (li) {
          li.classList.remove('slider__slide');
        });
        slider.classList.remove('slider-component-desktop');
        if (buttons) buttons.remove();
      }

      // Inserting the clone is what upgrades <slider-component>, so it
      // connects once, with the real cards already in place.
      slot.replaceWith(frag);
      root.style.display = '';
      root.removeAttribute('hidden');
    });
  }

  function init() {
    record();
    Array.prototype.forEach.call(
      document.querySelectorAll('[data-hcs-rv]'),
      render
    );
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
